import { useState, useEffect, useCallback } from 'react';
import Spinner from './Spinner';

// BSW Proformaları — Tedarik Takibi'nin (ScanInbound.jsx) parçaları: oturum listesinden açılan
// "BSW'ye gönder" paneli, oturum etiketleri ve "BSW Proformaları" sekmesi.
// Kaynağı BSW olan okutmalar BSW'ye gönderilir; BSW fiyatlayıp onaylayınca proforma gelir.
// Kurallar: database/017_bsw_proforma.sql

const BASE = '/api/bsw-proforma';
function api(method, path, body) {
  const token = localStorage.getItem('ozon_token');
  return fetch(`${BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  }).then(async r => {
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.error || r.statusText);
    return d;
  });
}

async function indir(path, dosya) {
  const token = localStorage.getItem('ozon_token');
  const r = await fetch(`${BASE}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!r.ok) throw new Error('İndirilemedi');
  const url = URL.createObjectURL(await r.blob());
  const a = document.createElement('a');
  a.href = url; a.download = dosya; a.click();
  URL.revokeObjectURL(url);
}

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleString('tr-TR', {
    timeZone: 'Europe/Moscow', day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

// '1234.5000' → '1.234,50' (float'a çevrilmez)
function tl(metin) {
  if (metin === null || metin === undefined) return '—';
  const s = String(metin);
  const eksi = s.startsWith('-');
  const [tam, kesir = ''] = s.replace(/^[-+]/, '').split('.');
  let k = kesir.replace(/0+$/, '');
  if (k.length < 2) k = k.padEnd(2, '0');
  return `${eksi ? '-' : ''}${(tam || '0').replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${k}`;
}

export function durumu(t) {
  if (t.durum === 'proforma' && t.okutma > 0 && t.odenen === t.okutma) return { ad: 'Ödendi', cls: 'bg-blue-100 text-blue-800' };
  return {
    gonderiliyor: { ad: 'Gönderiliyor',      cls: 'bg-slate-100 text-slate-700' },
    hata:         { ad: 'Gönderilemedi',     cls: 'bg-red-100 text-red-700' },
    gonderildi:   { ad: "BSW'de bekliyor",   cls: 'bg-amber-100 text-amber-800' },
    proforma:     { ad: 'Proforma geldi',    cls: 'bg-emerald-100 text-emerald-800' },
    reddedildi:   { ad: 'Reddedildi',        cls: 'bg-red-100 text-red-700' },
  }[t.durum];
}
const TUR_AD = { urun: 'Ürün', indirim: 'İndirim', hizmet: 'Hizmet bedeli' };

export function BswGonder({ onGonderildi, onKapat }) {
  const [oturumlar, setOturumlar] = useState(null);
  const [secili, setSecili] = useState(new Set());
  const [onizleme, setOnizleme] = useState(null);
  const [notMetni, setNotMetni] = useState('');
  const [mesgul, setMesgul] = useState(false);
  const [hataMsg, setHataMsg] = useState('');

  const yukle = useCallback(async () => {
    const o = await api('GET', '/bekleyen');
    setOturumlar(o); setSecili(new Set()); setOnizleme(null);
  }, []);
  useEffect(() => { yukle().catch(e => setHataMsg(e.message)); }, [yukle]);

  useEffect(() => {
    if (!secili.size) { setOnizleme(null); return; }
    api('POST', '/onizleme', { oturumlar: [...secili] }).then(setOnizleme).catch(e => setHataMsg(e.message));
  }, [secili]);

  function degistir(id) {
    setSecili(s => { const y = new Set(s); y.has(id) ? y.delete(id) : y.add(id); return y; });
  }

  async function gonder() {
    if (!onizleme?.kalemler.length) return;
    const adet = onizleme.kalemler.reduce((a, k) => a + k.adet, 0);
    if (!window.confirm(`${onizleme.kalemler.length} ürün, ${adet} adet BSW'ye gönderilsin mi? Gönderilen okutmalar talep sonuçlanana kadar değiştirilemez.`)) return;
    setMesgul(true); setHataMsg('');
    try {
      const t = await api('POST', '/talepler', { oturumlar: [...secili], not: notMetni });
      if (t.durum === 'hata') setHataMsg(`Talep kaydedildi ama BSW'ye ulaşılamadı: ${t.hata_metni}. Aşağıdan tekrar gönderebilirsin.`);
      setNotMetni('');
      await yukle(); onGonderildi();
    } catch (e) { setHataMsg(e.message); }
    setMesgul(false);
  }

  if (!oturumlar) return <Spinner />;
  return (
    <div className="bg-white rounded-xl border p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold">BSW'ye gönderilmemiş okutmalar</h2>
        {onKapat && <button onClick={onKapat} className="text-sm text-slate-500 hover:text-slate-800">✕ Kapat</button>}
      </div>
      {hataMsg && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{hataMsg}</div>}
      {oturumlar.length === 0 ? (
        <p className="text-sm text-slate-500">Gönderilecek BSW okutması yok. Tedarik Takibi'nde kaynağı BSW olan okutmalar burada çıkar.</p>
      ) : (
        <>
          <div className="divide-y border rounded-lg">
            <label className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer bg-slate-50 font-medium">
              <input type="checkbox" checked={secili.size === oturumlar.length}
                     onChange={() => setSecili(secili.size === oturumlar.length ? new Set() : new Set(oturumlar.map(o => o.id)))} />
              <span className="flex-1">Tümünü seç</span>
              <span className="text-slate-600">{oturumlar.reduce((a, o) => a + o.okutma, 0)} okutma · {oturumlar.reduce((a, o) => a + o.adet, 0)} adet</span>
            </label>
            {oturumlar.map(o => (
              <label key={o.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                <input type="checkbox" checked={secili.has(o.id)} onChange={() => degistir(o.id)} />
                <span className="flex-1">{o.note || 'Oturum'} <span className="text-slate-400">· {formatDate(o.created_at)}</span></span>
                <span className="text-slate-600">{o.okutma} okutma · {o.urun} ürün · {o.adet} adet</span>
                {o.mukerrer > 0 && <span className="text-xs text-orange-700" title="Başka oturumda da okutulmuş; BSW'ye bir kez gider">+{o.mukerrer} mükerrer (gönderilmez)</span>}
              </label>
            ))}
          </div>
          {onizleme && (
            <div className="space-y-2">
              <div className="max-h-64 overflow-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-slate-500 border-b bg-slate-50">
                    <th className="px-2 py-1">Ürün kodu</th><th>Ürün</th><th className="text-right px-2">Adet</th>
                  </tr></thead>
                  <tbody>
                    {onizleme.kalemler.map(k => (
                      <tr key={k.offer_id} className="border-b border-slate-100">
                        <td className="px-2 py-1 font-mono text-xs">{k.offer_id}</td><td>{k.ad}</td>
                        <td className="text-right px-2">{k.adet}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <input value={notMetni} onChange={e => setNotMetni(e.target.value)} placeholder="BSW'ye not (isteğe bağlı)"
                       className="flex-1 min-w-[200px] border rounded px-2 py-1.5 text-sm" />
                <button onClick={gonder} disabled={mesgul || !onizleme.kalemler.length}
                        className="px-4 py-1.5 rounded bg-indigo-600 text-white text-sm disabled:opacity-40">
                  BSW'ye gönder ({onizleme.okutma} okutma)
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const bugun = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Moscow' });   // YYYY-MM-DD

async function belgeYukle(id, dosya) {
  const fd = new FormData();
  fd.append('belge', dosya);
  const r = await fetch(`${BASE}/talepler/${id}/belgeler`, {
    method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('ozon_token')}` }, body: fd,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'Belge yüklenemedi');
  return d;
}

// Ödeme: "Ödendi" → proformadaki okutmalar Tedarik Raporu'nda ödendi olur. Tarih, not, belge isteğe bağlı.
function OdemePaneli({ id, t, odendi, okutma, belgeler, mesgul, islem, setHataMsg }) {
  const [form, setForm] = useState(false);
  const [tarih, setTarih] = useState(bugun());
  const [notMetni, setNotMetni] = useState('');
  const [dosya, setDosya] = useState(null);

  function kaydet() {
    if (dosya && dosya.size > 10 * 1024 * 1024) { setHataMsg('Dosya 10 MB\'tan büyük'); return; }
    islem(async () => {
      await api('POST', `/talepler/${id}/odendi`, { odendi: true, tarih, not: notMetni });
      if (dosya) await belgeYukle(id, dosya);
      setForm(false); setDosya(null);
    });
  }
  function belgeEkle(e) {
    const f = e.target.files?.[0]; e.target.value = '';
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { setHataMsg('Dosya 10 MB\'tan büyük'); return; }
    islem(() => belgeYukle(id, f));
  }

  return (
    <div className={`rounded-lg border p-3 space-y-2 text-sm ${odendi ? 'border-blue-200 bg-blue-50/40' : 'border-slate-200'}`}>
      {odendi ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-blue-800">✓ Ödendi</span>
          {t.odeme_tarihi && <span className="text-slate-600">{new Date(t.odeme_tarihi).toLocaleDateString('tr-TR', { timeZone: 'UTC' })}</span>}
          {t.odendi_by && <span className="text-xs text-slate-400">· {t.odendi_by}</span>}
          {t.odeme_notu && <span className="text-slate-600">· {t.odeme_notu}</span>}
          <button disabled={mesgul} onClick={() => window.confirm('Ödendi işareti kaldırılsın mı? Okutmalar Tedarik Raporu\'nda tekrar ödenmedi görünür.') &&
                    islem(() => api('POST', `/talepler/${id}/odendi`, { odendi: false }))}
                  className="ml-auto text-xs px-2 py-1 rounded border border-blue-300 text-blue-700">geri al</button>
        </div>
      ) : !form ? (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-slate-600">Ödenmedi — {okutma} okutma Tedarik Raporu'nda ödenmedi görünüyor.</span>
          <button onClick={() => setForm(true)} className="ml-auto px-3 py-1 rounded bg-blue-600 text-white">Ödendi işaretle</button>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-slate-600">Ödeme tarihi</label>
            <input type="date" value={tarih} onChange={e => setTarih(e.target.value)} className="border rounded px-2 py-1" />
            <input value={notMetni} onChange={e => setNotMetni(e.target.value)} placeholder="Not (isteğe bağlı, ör. havale / dekont no)"
                   className="flex-1 min-w-[200px] border rounded px-2 py-1" />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-slate-600">Ödeme belgesi (isteğe bağlı)</label>
            <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={e => setDosya(e.target.files?.[0] || null)} className="text-xs" />
            <span className="text-xs text-slate-400">PDF/JPG/PNG, en fazla 10 MB</span>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setForm(false)} className="px-3 py-1 rounded border">Vazgeç</button>
            <button disabled={mesgul || !tarih} onClick={kaydet} className="px-3 py-1 rounded bg-blue-600 text-white disabled:opacity-40">
              Kaydet — {okutma} okutma ödendi olsun
            </button>
          </div>
        </div>
      )}
      {(belgeler.length > 0 || odendi) && (
        <div className="space-y-1">
          {belgeler.map(b => (
            <div key={b.id} className="flex items-center gap-2 text-xs">
              <span>📎</span>
              <button onClick={() => indir(`/talepler/${id}/belgeler/${b.id}`, b.dosya_adi).catch(e => setHataMsg(e.message))}
                      className="text-indigo-700 hover:underline truncate max-w-[260px]">{b.dosya_adi}</button>
              <span className="text-slate-400">{Math.ceil(b.boyut / 1024)} KB · {formatDate(b.yuklendi_at)}</span>
              <button disabled={mesgul} onClick={() => window.confirm(`${b.dosya_adi} silinsin mi?`) &&
                        islem(() => api('DELETE', `/talepler/${id}/belgeler/${b.id}`))}
                      className="text-red-500">sil</button>
            </div>
          ))}
          <label className="inline-block text-xs text-indigo-600 hover:underline cursor-pointer">
            + belge ekle
            <input type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={belgeEkle} className="hidden" />
          </label>
        </div>
      )}
    </div>
  );
}

function TalepDetay({ id, onDegisti }) {
  const [veri, setVeri] = useState(null);
  const [hataMsg, setHataMsg] = useState('');
  const [mesgul, setMesgul] = useState(false);
  const yukle = useCallback(() => api('GET', `/talepler/${id}`).then(setVeri).catch(e => setHataMsg(e.message)), [id]);
  useEffect(() => { yukle(); }, [yukle]);

  async function islem(fn) {
    setMesgul(true); setHataMsg('');
    try { await fn(); await yukle(); onDegisti(); } catch (e) { setHataMsg(e.message); }
    setMesgul(false);
  }

  if (!veri) return <div className="p-4"><Spinner /> {hataMsg}</div>;
  const { talep: t, okutmalar } = veri;
  const pf = t.proforma;
  const odenen = okutmalar.filter(o => o.paid).length;

  return (
    <div className="p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-semibold">Talep #{t.id}</h2>
        <span className="text-xs text-slate-500">{formatDate(t.created_at)} · {t.created_by}</span>
        {t.not_metni && <span className="text-xs text-slate-600">“{t.not_metni}”</span>}
      </div>
      {hataMsg && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{hataMsg}</div>}

      {t.durum === 'hata' && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-red-700">BSW'ye ulaşılamadı: {t.hata_metni}</span>
          <button disabled={mesgul} onClick={() => islem(() => api('POST', `/talepler/${id}/tekrar-gonder`))}
                  className="px-3 py-1 rounded bg-indigo-600 text-white">Tekrar gönder</button>
          <button disabled={mesgul} onClick={() => window.confirm('Talep iptal edilsin, okutmalar serbest kalsın mı?') &&
                    islem(() => api('POST', `/talepler/${id}/iptal`))}
                  className="px-3 py-1 rounded border">İptal et</button>
        </div>
      )}
      {t.durum === 'gonderildi' && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-amber-700">BSW fiyatlandırıp onaylayınca proforma burada görünecek.</span>
          <button disabled={mesgul} onClick={() => islem(() => api('POST', `/talepler/${id}/yenile`))}
                  className="px-3 py-1 rounded border">Durumu yenile</button>
        </div>
      )}
      {t.durum === 'reddedildi' && <div className="text-sm text-red-700">BSW reddetti: {t.red_notu}. Okutmalar tekrar gönderilebilir.</div>}

      {pf && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{pf.no}</span>
            <span className="text-sm text-slate-500">{formatDate(pf.created_at)}</span>
            <button onClick={() => indir(`/talepler/${id}/pdf`, `${pf.no}.pdf`).catch(e => setHataMsg(e.message))}
                    className="text-sm px-3 py-1 rounded bg-slate-800 text-white">PDF indir</button>
            <button onClick={() => indir(`/talepler/${id}/excel`, `${pf.no}.xlsx`).catch(e => setHataMsg(e.message))}
                    className="text-sm px-3 py-1 rounded bg-emerald-700 text-white">Excel indir</button>
          </div>
          <OdemePaneli id={id} t={t} odendi={odenen === okutmalar.length && okutmalar.length > 0}
                       okutma={okutmalar.length} belgeler={veri.belgeler} mesgul={mesgul} islem={islem} setHataMsg={setHataMsg} />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-500 border-b">
                <th className="py-1">#</th><th>Tür</th><th>Ürün kodu</th><th>Açıklama</th>
                <th className="text-right">Adet</th><th className="text-right">Birim fiyat</th><th className="text-right">Tutar</th>
              </tr></thead>
              <tbody>
                {pf.satirlar.map(s => (
                  <tr key={s.sira} className="border-b border-slate-100">
                    <td className="py-1">{s.sira}</td><td>{TUR_AD[s.tur]}</td>
                    <td className="font-mono text-xs">{s.onun_offer_id}</td><td>{s.aciklama}</td>
                    <td className="text-right">{s.adet}</td><td className="text-right">{tl(s.birim_fiyat)}</td>
                    <td className="text-right">{tl(s.tutar)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr><td colSpan={6} className="text-right font-semibold py-2">Toplam</td>
                <td className="text-right font-semibold">{tl(pf.toplam)} TL</td></tr></tfoot>
            </table>
          </div>
        </div>
      )}

      {!pf && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-slate-500 border-b"><th className="py-1">Ürün kodu</th><th>Ürün</th><th className="text-right">Adet</th></tr></thead>
            <tbody>
              {t.kalemler.map(k => (
                <tr key={k.offer_id} className="border-b border-slate-100">
                  <td className="py-1 font-mono text-xs">{k.offer_id}</td><td>{k.ad}</td><td className="text-right">{k.adet}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {okutmalar.length > 0 && (
        <details className="text-sm">
          <summary className="cursor-pointer text-slate-600">{okutmalar.length} okutma ({odenen} ödendi)</summary>
          <div className="mt-2 max-h-56 overflow-auto text-xs space-y-0.5">
            {okutmalar.map(o => (
              <div key={o.id} className="flex gap-3">
                <span className="font-mono">{o.posting_number}</span>
                <span className="text-slate-400">{o.oturum_notu || `oturum ${o.oturum}`} · {formatDate(o.scanned_at)}</span>
                {o.paid && <span className="text-blue-700">ödendi</span>}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// Tedarik Takibi → "BSW Proformaları" sekmesi
export function BswProformaListesi() {
  const [liste, setListe] = useState(null);
  const [kopruHatasi, setKopruHatasi] = useState(null);
  const [secili, setSecili] = useState(null);
  const [hataMsg, setHataMsg] = useState('');

  const yukle = useCallback(() => api('GET', '/talepler')
    .then(d => { setListe(d.talepler); setKopruHatasi(d.kopru_hatasi); })
    .catch(e => setHataMsg(e.message)), []);
  useEffect(() => { yukle(); }, [yukle]);

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-500">BSW'ye gönderilen okutmalar. BSW fiyatlayıp onaylayınca proforma burada görünür. Göndermek için Tedarik Oturumları'ndaki "BSW'ye gönder" düğmesini kullan.</p>
      {hataMsg && <div className="text-sm text-red-700">{hataMsg}</div>}
      {kopruHatasi && <div className="text-sm text-amber-700">BSW'ye şu an ulaşılamıyor ({kopruHatasi}); durumlar sonra yenilenecek.</div>}
      {!liste ? <Spinner /> : (
        <div className="grid md:grid-cols-[300px_1fr] gap-4">
          <div className="bg-white rounded-xl border divide-y max-h-[70vh] overflow-auto">
            {liste.length === 0 && <div className="p-4 text-sm text-slate-500">Henüz talep yok.</div>}
            {liste.map(t => {
              const d = durumu(t);
              return (
                <button key={t.id} onClick={() => setSecili(t.id)}
                        className={`block w-full text-left p-3 hover:bg-slate-50 ${secili === t.id ? 'bg-indigo-50' : ''}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">Talep #{t.id}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${d.cls}`}>{d.ad}</span>
                  </div>
                  <div className="text-xs text-slate-500">{formatDate(t.created_at)} · {t.urun} ürün · {t.adet} adet</div>
                  {t.proforma_no && <div className="text-xs text-emerald-700">{t.proforma_no} · {tl(t.toplam)} TL{t.belge > 0 ? ` · 📎 ${t.belge}` : ''}</div>}
                </button>
              );
            })}
          </div>
          <div className="bg-white rounded-xl border min-h-[160px] min-w-0">
            {secili ? <TalepDetay key={secili} id={secili} onDegisti={yukle} />
                    : <div className="p-6 text-sm text-slate-500">Soldan bir talep seç.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
